// useDragAndDrop.js
import { useState, useCallback, useRef, useEffect } from "react";
import {
  getWidgetPosition,
  getWidgetViewportPosition,
} from "@/utils/widgetUtils";

const ANIMATION_DURATION_MS = 450;
const DROP_ANIMATION_EASING = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
const DRAG_THRESHOLD = 5;

const findOverlappingWidget = (
  mouseX,
  mouseY,
  items,
  draggedItem,
  previewMode,
  containerWidth,
  gapPixels,
  totalColumns
) => {
  // Calculate grid dimensions
  const totalGapWidth = gapPixels * (totalColumns - 1);
  const columnWidth = (containerWidth - totalGapWidth) / totalColumns;
  const unitHeight = columnWidth;

  // Get dragged item dimensions
  const draggedCols = draggedItem.layout[previewMode]?.columns || 4;
  const draggedRows =
    draggedItem.layout[previewMode]?.columnHeight || draggedCols;
  const draggedCurrentPos = draggedItem.layout[previewMode]?.position || 0;

  // Calculate target position in grid units
  const targetCol = Math.floor(mouseX / (columnWidth + gapPixels));
  const targetRow = Math.floor(mouseY / (unitHeight + gapPixels));

  // Ensure column is even for proper alignment and never negative
  const adjustedTargetCol = Math.max(
    0,
    Math.min(Math.floor(targetCol / 2) * 2, totalColumns - draggedCols)
  );

  // Determine if this is a downward or upward drag
  const isDraggingDown = targetRow > draggedCurrentPos;
  const isDraggingUp = targetRow < draggedCurrentPos;

  // Generate a grid occupation map to track which positions are occupied
  const gridMap = new Map();
  const widgetsByPosition = new Map();

  // For each item, mark its occupied cells in the grid
  items.forEach((item) => {
    if (item.id === draggedItem.id) return; // Skip the dragged item
    if (item.layout?.settings?.locked) return; // Skip locked widgets

    const itemPos = item.layout[previewMode]?.position || 0;
    const itemHeight = item.layout[previewMode]?.columnHeight || 4;
    const itemStartCol = item.layout[previewMode]?.startColumn || 0;
    const itemCols = item.layout[previewMode]?.columns || 4;

    // Store widget by its position for easier lookup
    if (!widgetsByPosition.has(itemPos)) {
      widgetsByPosition.set(itemPos, []);
    }
    widgetsByPosition.get(itemPos).push(item);

    // Mark all cells this item occupies
    for (let row = itemPos; row < itemPos + itemHeight; row++) {
      for (let col = itemStartCol; col < itemStartCol + itemCols; col++) {
        const key = `${row},${col}`;
        gridMap.set(key, item.id);
      }
    }
  });

  // Function to check if a position is free for the dragged item
  const isPositionFree = (row, col, width, height) => {
    for (let r = row; r < row + height; r++) {
      for (let c = col; c < col + width; c++) {
        const key = `${r},${c}`;
        if (gridMap.has(key)) return false;
      }
    }
    return true;
  };

  // First, check for direct widget overlaps with significant coverage
  for (const item of items) {
    if (item.id === draggedItem.id) continue;
    if (item.layout?.settings?.locked) continue;

    const itemStartCol = item.layout[previewMode]?.startColumn || 0;
    const itemCols = item.layout[previewMode]?.columns || 4;
    const itemPos = item.layout[previewMode]?.position || 0;
    const itemRows = item.layout[previewMode]?.columnHeight || itemCols;

    // Check for overlap with significant coverage
    const hasHorizontalOverlap = !(
      adjustedTargetCol + draggedCols <= itemStartCol ||
      adjustedTargetCol >= itemStartCol + itemCols
    );

    const hasVerticalOverlap = !(
      targetRow + draggedRows <= itemPos || targetRow >= itemPos + itemRows
    );

    // If we have clear overlap and same size, swap widgets
    if (hasHorizontalOverlap && hasVerticalOverlap) {
      const isSameSize = draggedCols === itemCols && draggedRows === itemRows;
      const overlapPercentage = calculateOverlapPercentage(
        adjustedTargetCol,
        targetRow,
        draggedCols,
        draggedRows,
        itemStartCol,
        itemPos,
        itemCols,
        itemRows
      );

      // If same size or significant overlap, indicate widget swap
      if (isSameSize || overlapPercentage > 0.5) {
        return {
          widget: item,
          targetPosition: {
            startColumn: adjustedTargetCol,
            position: targetRow,
          },
          isDraggingDown,
          isDraggingUp,
        };
      }
    }
  }

  // Check if we can stay at the current row first (only for primarily horizontal movements)
  // Only prioritize current row if the target row is close to current row (within 1 unit)
  const isHorizontalMovement = Math.abs(targetRow - draggedCurrentPos) <= 1;

  if (
    isHorizontalMovement &&
    isPositionFree(
      draggedCurrentPos,
      adjustedTargetCol,
      draggedCols,
      draggedRows
    )
  ) {
    return {
      emptySpace: {
        startColumn: adjustedTargetCol,
        position: draggedCurrentPos,
        width: draggedCols,
        height: draggedRows,
        insertionType: "horizontal_move",
      },
    };
  }

  // Enhanced logic for downward dragging
  if (isDraggingDown) {
    // When dragging down, we want to find the best insertion point
    // Check if we can insert at the target position
    if (
      isPositionFree(targetRow, adjustedTargetCol, draggedCols, draggedRows)
    ) {
      return {
        emptySpace: {
          startColumn: adjustedTargetCol,
          position: targetRow,
          width: draggedCols,
          height: draggedRows,
          insertionType: "direct",
        },
        isDraggingDown: true,
      };
    }

    // If target position is occupied, find the next available slot below
    let searchRow = targetRow;
    const maxSearchDistance = 10; // Limit search to prevent infinite loops

    for (let offset = 0; offset <= maxSearchDistance; offset++) {
      const testRow = targetRow + offset;
      if (
        isPositionFree(testRow, adjustedTargetCol, draggedCols, draggedRows)
      ) {
        // Found a free position, check if there are widgets between dragged position and target
        const widgetsToShift = [];

        // Find widgets that are between the dragged widget's current position and the target
        items.forEach((item) => {
          if (item.id === draggedItem.id) return;
          if (item.layout?.settings?.locked) return;

          const itemPos = item.layout[previewMode]?.position || 0;
          const itemStartCol = item.layout[previewMode]?.startColumn || 0;
          const itemCols = item.layout[previewMode]?.columns || 4;

          // Check if widget is in the path of the drag and overlaps horizontally
          const isInDragPath =
            itemPos > draggedCurrentPos && itemPos <= testRow;
          const hasHorizontalOverlap = !(
            adjustedTargetCol + draggedCols <= itemStartCol ||
            adjustedTargetCol >= itemStartCol + itemCols
          );

          if (isInDragPath && hasHorizontalOverlap) {
            widgetsToShift.push(item);
          }
        });

        return {
          emptySpace: {
            startColumn: adjustedTargetCol,
            position: testRow,
            width: draggedCols,
            height: draggedRows,
            insertionType: "shift_down",
            widgetsToShift,
          },
          isDraggingDown: true,
        };
      }
    }
  }

  // Enhanced logic for upward dragging (keep existing good behavior)
  if (isDraggingUp) {
    // When dragging up, try to find the closest valid position above
    let searchRow = targetRow;
    while (searchRow >= 0) {
      if (
        isPositionFree(searchRow, adjustedTargetCol, draggedCols, draggedRows)
      ) {
        return {
          emptySpace: {
            startColumn: adjustedTargetCol,
            position: searchRow,
            width: draggedCols,
            height: draggedRows,
            insertionType: "direct",
          },
          isDraggingUp: true,
        };
      }
      searchRow--;
    }
  }

  // If the exact target position is free, use it (fallback for non-directional drags)
  if (isPositionFree(targetRow, adjustedTargetCol, draggedCols, draggedRows)) {
    return {
      emptySpace: {
        startColumn: adjustedTargetCol,
        position: targetRow,
        width: draggedCols,
        height: draggedRows,
        insertionType: "direct",
      },
    };
  }

  // Check if there are widgets at the same position that would be horizontally overlapped
  const overlappingWidgets = items.filter((item) => {
    if (item.id === draggedItem.id) return false;
    if (item.layout?.settings?.locked) return false;

    const itemPos = item.layout[previewMode]?.position || 0;
    const itemStartCol = item.layout[previewMode]?.startColumn || 0;
    const itemCols = item.layout[previewMode]?.columns || 4;

    const samePosition = itemPos === targetRow;
    const horizontalOverlap = !(
      adjustedTargetCol + draggedCols <= itemStartCol ||
      adjustedTargetCol >= itemStartCol + itemCols
    );

    return samePosition && horizontalOverlap;
  });

  // If there are horizontal overlaps at the target position, return the first one
  // to trigger our shift algorithm
  if (overlappingWidgets.length > 0) {
    return {
      emptySpace: {
        startColumn: adjustedTargetCol,
        position: targetRow,
        width: draggedCols,
        height: draggedRows,
        willShift: true, // Flag that this will cause widgets to shift
      },
    };
  }

  // Find all grid positions that would work for this widget
  const potentialPositions = [];

  // Find closest valid position considering the mouse position
  for (let row = 0; row < 100; row++) {
    // Arbitrary limit
    if (isPositionFree(row, adjustedTargetCol, draggedCols, draggedRows)) {
      potentialPositions.push({
        row,
        col: adjustedTargetCol,
        distance: Math.abs(targetRow - row),
      });
    }
  }

  // Sort by distance to target row
  potentialPositions.sort((a, b) => a.distance - b.distance);

  // Return the closest valid position
  if (potentialPositions.length > 0) {
    return {
      emptySpace: {
        startColumn: potentialPositions[0].col,
        position: potentialPositions[0].row,
        width: draggedCols,
        height: draggedRows,
        insertionType: "closest_available",
      },
    };
  }

  // Fallback to a reasonable default if nothing else works
  return {
    emptySpace: {
      startColumn: Math.max(
        0,
        Math.min(adjustedTargetCol, totalColumns - draggedCols)
      ),
      position: Math.max(0, targetRow),
      width: draggedCols,
      height: draggedRows,
      insertionType: "fallback",
    },
  };
};

// Helper function to calculate overlap percentage
const calculateOverlapPercentage = (
  x1,
  y1,
  width1,
  height1,
  x2,
  y2,
  width2,
  height2
) => {
  // Calculate overlap area
  const overlapLeft = Math.max(x1, x2);
  const overlapTop = Math.max(y1, y2);
  const overlapRight = Math.min(x1 + width1, x2 + width2);
  const overlapBottom = Math.min(y1 + height1, y2 + height2);

  // If no overlap
  if (overlapRight <= overlapLeft || overlapBottom <= overlapTop) {
    return 0;
  }

  // Calculate areas
  const overlapArea =
    (overlapRight - overlapLeft) * (overlapBottom - overlapTop);
  const area1 = width1 * height1;
  const area2 = width2 * height2;

  // Return percentage of smaller widget that's overlapped
  return overlapArea / Math.min(area1, area2);
};

// Export the compactLayoutVertically function
export const compactLayoutVertically = (
  layout,
  previewMode,
  totalColumns,
  excludedIds = []
) => {
  if (!layout || layout.length === 0) return layout;

  // Create a deep copy of the layout to work with
  const compactedLayout = JSON.parse(JSON.stringify(layout));

  // Create a map to track all widget positions for efficient collision detection
  const widgetMap = new Map();
  const lockedWidgets = [];
  const fixedWidgets = new Set(excludedIds);

  // Build initial widget map
  compactedLayout.forEach((item) => {
    const itemLayout = item.layout[previewMode];
    const itemPos = itemLayout?.position || 0;
    const itemHeight = itemLayout?.columnHeight || 4;
    const itemStartCol = itemLayout?.startColumn || 0;
    const itemCols = itemLayout?.columns || 4;

    const widgetInfo = {
      id: item.id,
      position: itemPos,
      height: itemHeight,
      startColumn: itemStartCol,
      columns: itemCols,
      locked: item.layout?.settings?.locked || false,
      excluded: excludedIds.includes(item.id),
      item: item,
    };

    widgetMap.set(item.id, widgetInfo);

    if (widgetInfo.locked || widgetInfo.excluded) {
      lockedWidgets.push(widgetInfo);
      fixedWidgets.add(item.id);
    }
  });

  // Sort widgets by position (top to bottom) for proper compaction order
  const sortedWidgets = Array.from(widgetMap.values())
    .filter((w) => !fixedWidgets.has(w.id))
    .sort((a, b) => {
      // If positions are equal, sort by startColumn for consistent ordering
      if (a.position === b.position) {
        return a.startColumn - b.startColumn;
      }
      return a.position - b.position;
    });

  // Helper function to check if a position is free (improved to use real-time widget map)
  const isPositionFree = (widgetId, row, startCol, cols, height) => {
    // Check each cell the widget would occupy
    for (let r = row; r < row + height; r++) {
      for (let c = startCol; c < startCol + cols; c++) {
        // Check against all other widgets using current positions from widgetMap
        for (const [otherId, otherWidget] of widgetMap) {
          if (otherId === widgetId) continue;

          // Check if this cell is occupied by the other widget
          const otherOccupiesCell =
            r >= otherWidget.position &&
            r < otherWidget.position + otherWidget.height &&
            c >= otherWidget.startColumn &&
            c < otherWidget.startColumn + otherWidget.columns;

          if (otherOccupiesCell) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Process each widget to compact it upward
  sortedWidgets.forEach((widget) => {
    // Skip if position is already 0
    if (widget.position <= 0) return;

    // Find the highest position this widget can move to
    let newPosition = widget.position;

    // Try moving up one row at a time
    for (let testPos = widget.position - 1; testPos >= 0; testPos--) {
      if (
        isPositionFree(
          widget.id,
          testPos,
          widget.startColumn,
          widget.columns,
          widget.height
        )
      ) {
        newPosition = testPos;
      } else {
        // Can't move any higher
        break;
      }
    }

    // If we found a better position, update it
    if (newPosition < widget.position) {
      widget.position = newPosition;
      widget.item.layout[previewMode] = {
        ...widget.item.layout[previewMode],
        position: newPosition,
      };

      // CRITICAL: Update the widget map with new position immediately
      widgetMap.set(widget.id, widget);
    }
  });

  // Enhanced final safety pass: ensure no widgets overlap with fixed widgets or each other
  const movedWidgets = new Map();
  let hasConflicts = true;
  let iterations = 0;
  const maxIterations = 10;

  while (hasConflicts && iterations < maxIterations) {
    hasConflicts = false;
    iterations++;

    compactedLayout.forEach((item) => {
      // Skip fixed widgets
      if (fixedWidgets.has(item.id)) return;

      const widget = widgetMap.get(item.id);
      if (!widget) return;

      let hasOverlap = false;
      let maxPushDown = widget.position;

      // Check for overlaps with fixed widgets
      for (const fixedWidget of lockedWidgets) {
        const hasVerticalOverlap = !(
          widget.position + widget.height <= fixedWidget.position ||
          widget.position >= fixedWidget.position + fixedWidget.height
        );

        const hasHorizontalOverlap = !(
          widget.startColumn + widget.columns <= fixedWidget.startColumn ||
          widget.startColumn >= fixedWidget.startColumn + fixedWidget.columns
        );

        if (hasVerticalOverlap && hasHorizontalOverlap) {
          hasOverlap = true;
          maxPushDown = Math.max(
            maxPushDown,
            fixedWidget.position + fixedWidget.height
          );
        }
      }

      // ENHANCED: Check for overlaps with other non-fixed widgets
      widgetMap.forEach((otherWidget, otherId) => {
        if (otherId === widget.id || fixedWidgets.has(otherId)) return;

        const hasVerticalOverlap = !(
          widget.position + widget.height <= otherWidget.position ||
          widget.position >= otherWidget.position + otherWidget.height
        );

        const hasHorizontalOverlap = !(
          widget.startColumn + widget.columns <= otherWidget.startColumn ||
          widget.startColumn >= otherWidget.startColumn + otherWidget.columns
        );

        // If there's an overlap and the other widget is positioned above or at same level
        if (
          hasVerticalOverlap &&
          hasHorizontalOverlap &&
          otherWidget.position <= widget.position
        ) {
          hasOverlap = true;
          maxPushDown = Math.max(
            maxPushDown,
            otherWidget.position + otherWidget.height
          );
        }
      });

      // If there's an overlap, update the position
      if (hasOverlap && maxPushDown > widget.position) {
        hasConflicts = true;
        widget.position = maxPushDown;
        widget.item.layout[previewMode] = {
          ...widget.item.layout[previewMode],
          position: maxPushDown,
        };

        // Update widget map immediately
        widgetMap.set(widget.id, widget);
        movedWidgets.set(item.id, maxPushDown);
      }
    });
  }

  return compactedLayout;
};

// Enhanced helper function to handle widget repositioning for both upward and downward drags
const repositionWidgetsForDrag = (
  targetPosition,
  startColumn,
  columns,
  height,
  draggedItemId,
  originalPosition,
  items,
  previewMode,
  isDraggingDown = false
) => {
  // Ensure minimum position is 0
  targetPosition = Math.max(0, targetPosition);
  originalPosition = Math.max(0, originalPosition);

  // Create a map to track all widget positions for efficient collision detection
  const widgetMap = new Map();
  const lockedWidgets = [];
  const movedWidgets = new Map();

  // Build initial widget map
  items.forEach((item) => {
    if (item.id === draggedItemId) return;

    const itemLayout = item.layout[previewMode];
    const itemPos = itemLayout?.position || 0;
    const itemHeight = itemLayout?.columnHeight || 4;
    const itemStartCol = itemLayout?.startColumn || 0;
    const itemCols = itemLayout?.columns || 4;

    const widgetInfo = {
      id: item.id,
      position: itemPos,
      height: itemHeight,
      startColumn: itemStartCol,
      columns: itemCols,
      locked: item.layout?.settings?.locked || false,
      item: item,
    };

    widgetMap.set(item.id, widgetInfo);

    if (widgetInfo.locked) {
      lockedWidgets.push(widgetInfo);
    }
  });

  // Check if the target position would overlap with any locked widgets
  let adjustedPosition = targetPosition;

  for (const lockedWidget of lockedWidgets) {
    const hasVerticalOverlap = !(
      adjustedPosition + height <= lockedWidget.position ||
      adjustedPosition >= lockedWidget.position + lockedWidget.height
    );

    const hasHorizontalOverlap = !(
      startColumn + columns <= lockedWidget.startColumn ||
      startColumn >= lockedWidget.startColumn + lockedWidget.columns
    );

    if (hasVerticalOverlap && hasHorizontalOverlap) {
      // Adjust position to be below the locked widget
      adjustedPosition = Math.max(
        adjustedPosition,
        lockedWidget.position + lockedWidget.height
      );
    }
  }

  // Special handling for downward dragging to find gaps
  if (isDraggingDown && adjustedPosition > originalPosition) {
    // When dragging down, try to find gaps between widgets
    const widgetsInPath = [];

    widgetMap.forEach((widget) => {
      if (widget.locked) return;

      const hasHorizontalOverlap = !(
        startColumn + columns <= widget.startColumn ||
        startColumn >= widget.startColumn + widget.columns
      );

      // Find widgets that are in the path between original and target position
      if (hasHorizontalOverlap && widget.position >= originalPosition) {
        widgetsInPath.push(widget);
      }
    });

    // Sort widgets by position
    widgetsInPath.sort((a, b) => a.position - b.position);

    // Try to find a gap where the widget can fit
    let bestPosition = adjustedPosition;
    for (let i = 0; i < widgetsInPath.length; i++) {
      const currentWidget = widgetsInPath[i];
      const nextWidget = widgetsInPath[i + 1];

      // Check if there's a gap after the current widget
      const gapStart = currentWidget.position + currentWidget.height;
      const gapEnd = nextWidget ? nextWidget.position : gapStart + height + 10; // Add buffer if no next widget

      // If the gap is big enough for our widget
      if (gapEnd - gapStart >= height) {
        // Check if this gap is closer to our target than pushing everything down
        if (gapStart <= adjustedPosition && gapStart >= originalPosition) {
          bestPosition = gapStart;
          break;
        }
      }
    }

    // If we found a better position (gap), use it without moving other widgets
    if (bestPosition !== adjustedPosition) {
      adjustedPosition = bestPosition;
      // Found a gap - no need to move other widgets!
    } else {
      // No suitable gap found, check if we absolutely need to push widgets
      // Only push if there's a direct collision that can't be avoided
      const directCollisions = [];

      widgetMap.forEach((widget) => {
        if (widget.locked) return;

        const hasVerticalOverlap = !(
          adjustedPosition + height <= widget.position ||
          adjustedPosition >= widget.position + widget.height
        );

        const hasHorizontalOverlap = !(
          startColumn + columns <= widget.startColumn ||
          startColumn >= widget.startColumn + widget.columns
        );

        // Only consider it a collision if widgets actually overlap
        if (hasVerticalOverlap && hasHorizontalOverlap) {
          directCollisions.push(widget);
        }
      });

      // If there are direct collisions, try to find alternative positions first
      if (directCollisions.length > 0) {
        // Try to find the lowest position that avoids all collisions
        const allWidgetPositions = Array.from(widgetMap.values())
          .filter((w) => !w.locked)
          .map((w) => ({
            start: w.position,
            end: w.position + w.height,
            widget: w,
          }))
          .sort((a, b) => a.start - b.start);

        let testPosition = adjustedPosition;
        let foundClearPosition = false;

        // Look for a clear position by checking between widgets
        for (let i = 0; i < allWidgetPositions.length; i++) {
          const current = allWidgetPositions[i];
          const next = allWidgetPositions[i + 1];

          // Skip if this position is before our desired position
          if (current.end < adjustedPosition) continue;

          // Check if we can fit between current and next widget
          const gapStart = Math.max(current.end, adjustedPosition);
          const gapEnd = next ? next.start : gapStart + height + 10;

          if (gapEnd - gapStart >= height) {
            // Check horizontal overlap
            const hasHorizontalOverlap = !(
              startColumn + columns <= current.widget.startColumn ||
              startColumn >= current.widget.startColumn + current.widget.columns
            );

            if (!hasHorizontalOverlap || gapStart >= current.end) {
              adjustedPosition = gapStart;
              foundClearPosition = true;
              break;
            }
          }
        }

        // Only if we absolutely can't find a gap, then push widgets minimally
        if (!foundClearPosition) {
          directCollisions.forEach((widget) => {
            const pushAmount = adjustedPosition + height - widget.position;
            if (pushAmount > 0) {
              movedWidgets.set(widget.id, widget.position + pushAmount);
            }
          });
        }
      }
    }
  } else {
    // Standard pushing logic for upward dragging or simple placement
    const processQueue = [];

    // Find widgets that need to be moved
    widgetMap.forEach((widget) => {
      if (widget.locked) return;

      const hasVerticalOverlap = !(
        adjustedPosition + height <= widget.position ||
        adjustedPosition >= widget.position + widget.height
      );

      const hasHorizontalOverlap = !(
        startColumn + columns <= widget.startColumn ||
        startColumn >= widget.startColumn + widget.columns
      );

      if (hasVerticalOverlap && hasHorizontalOverlap) {
        const pushAmount = adjustedPosition + height - widget.position;
        if (pushAmount > 0) {
          processQueue.push({
            widget: widget,
            newPosition: widget.position + pushAmount,
          });
        }
      }
    });

    // Process the queue, handling cascading conflicts
    while (processQueue.length > 0) {
      const { widget, newPosition } = processQueue.shift();

      // Skip if already processed with a larger push
      if (
        movedWidgets.has(widget.id) &&
        movedWidgets.get(widget.id) >= newPosition
      ) {
        continue;
      }

      // Check against locked widgets
      let finalPosition = newPosition;
      for (const lockedWidget of lockedWidgets) {
        const wouldOverlapVertically = !(
          finalPosition + widget.height <= lockedWidget.position ||
          finalPosition >= lockedWidget.position + lockedWidget.height
        );

        const wouldOverlapHorizontally = !(
          widget.startColumn + widget.columns <= lockedWidget.startColumn ||
          widget.startColumn >= lockedWidget.startColumn + lockedWidget.columns
        );

        if (wouldOverlapVertically && wouldOverlapHorizontally) {
          finalPosition = Math.max(
            finalPosition,
            lockedWidget.position + lockedWidget.height
          );
        }
      }

      movedWidgets.set(widget.id, finalPosition);

      // Check for new conflicts
      widgetMap.forEach((otherWidget) => {
        if (
          otherWidget.id === widget.id ||
          otherWidget.locked ||
          movedWidgets.has(otherWidget.id)
        ) {
          return;
        }

        const hasVerticalOverlap = !(
          finalPosition + widget.height <= otherWidget.position ||
          finalPosition >= otherWidget.position + otherWidget.height
        );

        const hasHorizontalOverlap = !(
          widget.startColumn + widget.columns <= otherWidget.startColumn ||
          widget.startColumn >= otherWidget.startColumn + otherWidget.columns
        );

        if (hasVerticalOverlap && hasHorizontalOverlap) {
          const otherPushAmount =
            finalPosition + widget.height - otherWidget.position;
          if (otherPushAmount > 0) {
            processQueue.push({
              widget: otherWidget,
              newPosition: otherWidget.position + otherPushAmount,
            });
          }
        }
      });
    }
  }

  // Apply all the moves
  movedWidgets.forEach((newPos, widgetId) => {
    const widget = widgetMap.get(widgetId);
    if (widget && widget.item) {
      widget.item.layout = {
        ...widget.item.layout,
        [previewMode]: {
          ...widget.item.layout[previewMode],
          position: Math.max(0, newPos),
        },
      };
    }
  });

  return { adjustedPosition, movedWidgets };
};

// Legacy function kept for backward compatibility
const pushDownOverlappingWidgets = (
  position,
  startColumn,
  columns,
  height,
  excludedIds = [],
  items,
  previewMode
) => {
  const draggedItemId = excludedIds[0] || null;
  const originalPosition =
    items.find((item) => item.id === draggedItemId)?.layout[previewMode]
      ?.position || 0;

  return repositionWidgetsForDrag(
    position,
    startColumn,
    columns,
    height,
    draggedItemId,
    originalPosition,
    items,
    previewMode,
    false
  );
};

export const useDragAndDrop = ({
  items,
  previewMode,
  handleWidgetUpdate,
  containerWidth,
  gapPixels = 24,
  totalColumns = 12,
  performMasonryLayout,
  isSettingsOpen = false,
  updateContainerHeight,
  isLightBackground = false,
}) => {
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragPosition, setDragPosition] = useState(null);
  const [currentLayout, setCurrentLayout] = useState(null);
  const [potentialDropArea, setPotentialDropArea] = useState(null);
  const [isDropAnimating, setIsDropAnimating] = useState(false);
  const [dropAnimationTarget, setDropAnimationTarget] = useState(null);

  const dragOffset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const initialMousePosition = useRef({ x: 0, y: 0 });
  const mouseMoved = useRef(false);
  const lastOverlapInfo = useRef(null);
  const originalPositions = useRef(null);
  const debouncedLayoutUpdateRef = useRef(null);
  const currentOverlapRef = useRef(null);

  const applyLayoutUpdate = useCallback(
    (overlap, draggedItem) => {
      if (!overlap || !draggedItem) return null;

      const updatedItems = [...(currentLayout || items)];
      const draggedLayout = draggedItem.layout[previewMode];
      const draggedCols = draggedLayout.columns;
      const draggedHeight = draggedLayout.columnHeight;
      const originalPosition = draggedLayout.position || 0;

      // Determine if this is a downward or upward drag
      const isDraggingDown = overlap.isDraggingDown || false;
      const isDraggingUp = overlap.isDraggingUp || false;

      if (overlap.widget) {
        // Handle widget overlap
        const overlappingWidget = overlap.widget;

        if (overlappingWidget.layout?.settings?.locked) {
          // If overlapping widget is locked, place dragged item below it
          const lockedPos =
            overlappingWidget.layout[previewMode]?.position || 0;
          const lockedHeight =
            overlappingWidget.layout[previewMode]?.columnHeight || 4;
          const targetPos = Math.max(0, lockedPos + lockedHeight);
          const targetStartCol = Math.max(0, draggedLayout.startColumn);

          // Update the dragged item's position
          updatedItems.forEach((item) => {
            if (item.id === draggedItem.id) {
              item.layout = {
                ...item.layout,
                [previewMode]: {
                  ...item.layout[previewMode],
                  position: targetPos,
                  startColumn: targetStartCol,
                },
              };
            }
          });

          // Use the new repositioning logic
          const result = repositionWidgetsForDrag(
            targetPos,
            targetStartCol,
            draggedCols,
            draggedHeight,
            draggedItem.id,
            originalPosition,
            updatedItems,
            previewMode,
            isDraggingDown
          );

          // Apply final adjusted position if needed
          if (result.adjustedPosition !== targetPos) {
            updatedItems.forEach((item) => {
              if (item.id === draggedItem.id) {
                item.layout = {
                  ...item.layout,
                  [previewMode]: {
                    ...item.layout[previewMode],
                    position: Math.max(0, result.adjustedPosition),
                  },
                };
              }
            });
          }
        } else {
          // Handle non-locked widget overlap
          const overlappingLayout = overlappingWidget.layout[previewMode];
          const isSameSize =
            draggedLayout.columns === overlappingLayout.columns &&
            draggedLayout.columnHeight === overlappingLayout.columnHeight;

          if (isSameSize) {
            // Swap positions for same-size widgets
            const draggedPos = Math.max(0, draggedLayout.position || 0);
            const draggedStartCol = Math.max(0, draggedLayout.startColumn);
            const overlapPos = Math.max(0, overlappingLayout.position || 0);
            const overlapStartCol = Math.max(0, overlappingLayout.startColumn);

            updatedItems.forEach((item) => {
              if (item.id === draggedItem.id) {
                item.layout = {
                  ...item.layout,
                  [previewMode]: {
                    ...item.layout[previewMode],
                    position: overlapPos,
                    startColumn: overlapStartCol,
                  },
                };
              } else if (item.id === overlappingWidget.id) {
                item.layout = {
                  ...item.layout,
                  [previewMode]: {
                    ...item.layout[previewMode],
                    position: draggedPos,
                    startColumn: draggedStartCol,
                  },
                };
              }
            });
          } else {
            // Different sizes - use enhanced repositioning
            const targetPos = Math.max(0, overlappingLayout.position || 0);
            const targetStartCol = Math.max(0, overlappingLayout.startColumn);

            // Place the dragged item at the target position
            updatedItems.forEach((item) => {
              if (item.id === draggedItem.id) {
                item.layout = {
                  ...item.layout,
                  [previewMode]: {
                    ...item.layout[previewMode],
                    position: targetPos,
                    startColumn: targetStartCol,
                  },
                };
              }
            });

            // Use enhanced repositioning logic
            const result = repositionWidgetsForDrag(
              targetPos,
              targetStartCol,
              draggedCols,
              draggedHeight,
              draggedItem.id,
              originalPosition,
              updatedItems,
              previewMode,
              isDraggingDown
            );

            // Apply final adjusted position if needed
            if (result.adjustedPosition !== targetPos) {
              updatedItems.forEach((item) => {
                if (item.id === draggedItem.id) {
                  item.layout = {
                    ...item.layout,
                    [previewMode]: {
                      ...item.layout[previewMode],
                      position: Math.max(0, result.adjustedPosition),
                    },
                  };
                }
              });
            }
          }
        }
      } else if (overlap.emptySpace) {
        // Handle empty space placement
        const newPosition = Math.max(0, overlap.emptySpace.position);
        const newStartColumn = Math.max(0, overlap.emptySpace.startColumn);

        // Update the dragged item's position
        updatedItems.forEach((item) => {
          if (item.id === draggedItem.id) {
            item.layout = {
              ...item.layout,
              [previewMode]: {
                ...item.layout[previewMode],
                position: newPosition,
                startColumn: newStartColumn,
              },
            };
          }
        });

        // Use enhanced repositioning logic based on drag direction
        const result = repositionWidgetsForDrag(
          newPosition,
          newStartColumn,
          draggedCols,
          draggedHeight,
          draggedItem.id,
          originalPosition,
          updatedItems,
          previewMode,
          isDraggingDown
        );

        // Apply final adjusted position if needed
        if (result.adjustedPosition !== newPosition) {
          updatedItems.forEach((item) => {
            if (item.id === draggedItem.id) {
              item.layout = {
                ...item.layout,
                [previewMode]: {
                  ...item.layout[previewMode],
                  position: Math.max(0, result.adjustedPosition),
                },
              };
            }
          });
        }
      }

      return updatedItems;
    },
    [items, currentLayout, previewMode]
  );

  // Debounced layout update to prevent jumpiness
  const debouncedLayoutUpdate = useCallback(
    (overlap, draggedItem) => {
      // Clear any existing timeout
      if (debouncedLayoutUpdateRef.current) {
        clearTimeout(debouncedLayoutUpdateRef.current);
      }

      // Store the current overlap for comparison
      currentOverlapRef.current = overlap;

      // Set a debounced timeout
      debouncedLayoutUpdateRef.current = setTimeout(() => {
        // Double-check that this is still the current overlap
        if (currentOverlapRef.current === overlap) {
          if (overlap) {
            const updatedLayout = applyLayoutUpdate(overlap, draggedItem);
            if (updatedLayout) {
              setCurrentLayout(updatedLayout);
              requestAnimationFrame(() => {
                performMasonryLayout(containerWidth, updatedLayout);
              });
            }
          } else {
            // No overlap - restore original positions
            if (originalPositions.current) {
              const restoredLayout = items.map((widget) => {
                const original = originalPositions.current[widget.id];
                if (!original) return widget;

                return {
                  ...widget,
                  layout: {
                    ...widget.layout,
                    [previewMode]: {
                      ...widget.layout[previewMode],
                      position: original.position,
                      startColumn: original.startColumn,
                    },
                  },
                };
              });
              setCurrentLayout(restoredLayout);
              requestAnimationFrame(() => {
                performMasonryLayout(containerWidth, restoredLayout);
              });
            }
          }
        }
      }, 150); // 150ms debounce delay
    },
    [
      items,
      previewMode,
      containerWidth,
      performMasonryLayout,
      applyLayoutUpdate,
    ]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging.current || !draggedItemId) return;
      e.preventDefault();

      const container = document.querySelector("[data-masonry-container]");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const draggedItem = items.find((item) => item.id === draggedItemId);
      if (!draggedItem) return;

      // Get coordinates from either mouse or touch event
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;

      if (clientX === undefined || clientY === undefined) return;

      // Update visual position of dragged item
      setDragPosition({
        x: clientX - dragOffset.current.x,
        y: clientY - dragOffset.current.y,
        width: dragPosition?.width,
        height: dragPosition?.height,
      });

      // Calculate position relative to container
      const mouseX = clientX - containerRect.left;
      const mouseY = clientY - containerRect.top + window.scrollY;

      // Find overlapping widget
      const overlap = findOverlappingWidget(
        mouseX,
        mouseY,
        currentLayout || items,
        draggedItem,
        previewMode,
        containerWidth,
        gapPixels,
        totalColumns
      );

      // Update drop area preview
      if (overlap) {
        const isSameOverlap =
          lastOverlapInfo.current &&
          ((overlap.emptySpace &&
            lastOverlapInfo.current.type === "empty" &&
            overlap.emptySpace.startColumn ===
              lastOverlapInfo.current.startColumn &&
            overlap.emptySpace.position === lastOverlapInfo.current.position) ||
            (overlap.widget &&
              lastOverlapInfo.current.type === "widget" &&
              overlap.widget.id === lastOverlapInfo.current.widgetId));

        if (!isSameOverlap) {
          if (overlap.emptySpace) {
            lastOverlapInfo.current = {
              type: "empty",
              startColumn: overlap.emptySpace.startColumn,
              position: overlap.emptySpace.position,
            };
            setPotentialDropArea({
              type: "empty",
              ...overlap.emptySpace,
            });
          } else if (overlap.widget) {
            lastOverlapInfo.current = {
              type: "widget",
              widgetId: overlap.widget.id,
            };
            const targetWidget = overlap.widget;
            setPotentialDropArea({
              type: "widget",
              startColumn: targetWidget.layout[previewMode].startColumn,
              position: targetWidget.layout[previewMode].position,
              width: targetWidget.layout[previewMode].columns || 4,
              height:
                targetWidget.layout[previewMode].columnHeight ||
                targetWidget.layout[previewMode].columns ||
                4,
            });
          }

          // Use debounced layout update to prevent jumpiness
          debouncedLayoutUpdate(overlap, draggedItem);
        }
      } else {
        lastOverlapInfo.current = null;
        setPotentialDropArea(null);
        // Use debounced update to restore original positions
        debouncedLayoutUpdate(null, draggedItem);
      }
    },
    [
      draggedItemId,
      items,
      previewMode,
      containerWidth,
      performMasonryLayout,
      dragPosition,
      currentLayout,
      totalColumns,
      gapPixels,
      debouncedLayoutUpdate,
    ]
  );

  const performDropLogic = useCallback(
    (draggedItem) => {
      setIsDropAnimating(false);
      setDropAnimationTarget(null);

      // Check if the widget was dropped at its original position
      const originalPos = originalPositions.current?.[draggedItem.id];
      const currentPos = draggedItem.layout[previewMode];
      const finalLayout = currentLayout || items;

      const isAtOriginalPosition =
        originalPos &&
        currentPos.position === originalPos.position &&
        currentPos.startColumn === originalPos.startColumn;

      // If dropped at original position, restore all widgets to their original positions
      if (isAtOriginalPosition && originalPositions.current) {
        // Create updated widgets with original positions
        const restoredWidgets = items.map((widget) => {
          const original = originalPositions.current[widget.id];
          if (!original) return widget;

          // Only update the layout for the current previewMode
          return {
            ...widget,
            layout: {
              ...widget.layout,
              [previewMode]: {
                ...widget.layout[previewMode],
                position: original.position,
                startColumn: original.startColumn,
              },
            },
          };
        });

        // Update the widgets in the store
        handleWidgetUpdate(null, {
          type: "UPDATE_WIDGET",
          widgets: restoredWidgets,
          triggerAutosave: true,
        });

        // Force layout recalculation
        setTimeout(() => {
          performMasonryLayout(containerWidth, restoredWidgets);
          if (updateContainerHeight) {
            updateContainerHeight();
          }
        }, ANIMATION_DURATION_MS + 100);
      } else if (currentLayout) {
        // Find which widgets actually changed position from original
        const changedWidgets = new Set();
        changedWidgets.add(draggedItemId); // Always include the dragged widget

        // Compare original and current positions to detect moved widgets
        finalLayout.forEach((widget) => {
          if (widget.id === draggedItemId) return; // Already included

          const originalPosition = originalPositions.current?.[widget.id];
          const currentPosition = widget.layout[previewMode];

          if (
            originalPosition &&
            (originalPosition.position !== currentPosition.position ||
              originalPosition.startColumn !== currentPosition.startColumn)
          ) {
            changedWidgets.add(widget.id);
          }
        });

        // Create hybrid layout: keep changes only for affected widgets and only for current previewMode
        const hybridLayout = items.map((widget) => {
          // If widget was affected by drag, keep its new position for current previewMode only
          if (changedWidgets.has(widget.id)) {
            const updatedWidget = finalLayout.find((w) => w.id === widget.id);
            return {
              ...widget,
              layout: {
                ...widget.layout,
                [previewMode]: {
                  ...widget.layout[previewMode],
                  position: updatedWidget.layout[previewMode].position,
                  startColumn: updatedWidget.layout[previewMode].startColumn,
                },
              },
            };
          }
          // If widget wasn't affected, restore its original position for current previewMode
          else if (originalPositions.current?.[widget.id]) {
            return {
              ...widget,
              layout: {
                ...widget.layout,
                [previewMode]: {
                  ...widget.layout[previewMode],
                  position: originalPositions.current[widget.id].position,
                  startColumn: originalPositions.current[widget.id].startColumn,
                },
              },
            };
          }
          // Fallback for any widgets without original positions
          return widget;
        });

        // Apply vertical compaction to create a more dense layout
        const compactedLayout = compactLayoutVertically(
          hybridLayout,
          previewMode,
          totalColumns
        );

        // Update the widgets in the store and save to database
        handleWidgetUpdate(null, {
          type: "UPDATE_WIDGET",
          widgets: compactedLayout,
          triggerAutosave: true,
        });

        // Force layout recalculation
        setTimeout(() => {
          performMasonryLayout(containerWidth, compactedLayout);
          if (updateContainerHeight) {
            updateContainerHeight();
          }
        }, ANIMATION_DURATION_MS + 100);
      }

      // Clean up debounced timeout
      if (debouncedLayoutUpdateRef.current) {
        clearTimeout(debouncedLayoutUpdateRef.current);
        debouncedLayoutUpdateRef.current = null;
      }

      // Reset drag state
      setDraggedItemId(null);
      setCurrentLayout(null);
      setPotentialDropArea(null);
      setDragPosition(null);
      originalPositions.current = null;
      currentOverlapRef.current = null;
    },
    [
      draggedItemId,
      currentLayout,
      handleWidgetUpdate,
      items,
      previewMode,
      containerWidth,
      performMasonryLayout,
      updateContainerHeight,
      totalColumns,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current || !draggedItemId) return;
    isDragging.current = false;

    const draggedItem = items.find((item) => item.id === draggedItemId);
    if (!draggedItem) {
      setDraggedItemId(null);
      setDragPosition(null);
      setCurrentLayout(null);
      setPotentialDropArea(null);
      return;
    }

    // Calculate the target position for the drop animation using actual masonry positions
    const container = document.querySelector("[data-masonry-container]");
    if (container && dragPosition && currentLayout) {
      // Find the target widget in the current layout to get its actual position
      const targetWidget = currentLayout.find((w) => w.id === draggedItem.id);
      if (targetWidget) {
        // Get the container's bounding rect to calculate relative positions
        const containerRect = container.getBoundingClientRect();

        // Calculate where the widget should be positioned in the masonry layout
        const finalPos = targetWidget.layout[previewMode];
        const totalGapWidth = gapPixels * (totalColumns - 1);
        const columnWidth = (containerWidth - totalGapWidth) / totalColumns;
        const unitHeight = columnWidth;

        // Calculate the target position relative to the container
        const targetLeft =
          (finalPos.startColumn || 0) * (columnWidth + gapPixels);
        const targetTop = (finalPos.position || 0) * (unitHeight + gapPixels);

        // Convert to screen coordinates (add container position + scroll)
        const targetX = containerRect.left + targetLeft;
        const targetY = containerRect.top + targetTop + window.scrollY;

        // Start drop animation
        setIsDropAnimating(true);
        setDropAnimationTarget({ x: targetX, y: targetY });

        // Animate the dragged item to its final position
        setDragPosition({
          ...dragPosition,
          x: targetX,
          y: targetY,
        });

        // After animation completes, finish the drop
        setTimeout(() => {
          performDropLogic(draggedItem);
        }, 300); // Match this to the CSS transition duration

        return;
      }
    }

    // Fallback if no container found
    performDropLogic(draggedItem);
  }, [
    items,
    dragPosition,
    previewMode,
    gapPixels,
    totalColumns,
    containerWidth,
    performDropLogic,
  ]);

  const handleMouseDown = useCallback(
    (e, itemId, bypassThreshold = false, customDragOffset = null) => {
      if (isSettingsOpen) return;

      const element = e.currentTarget.closest("[data-widget-id]");
      const item = items.find((i) => i.id === itemId);

      // Prevent dragging if widget is locked
      if (item?.layout?.settings?.locked) {
        return;
      }

      // Get coordinates from either mouse or touch event
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;

      if (clientX === undefined || clientY === undefined) return;

      // Record start position for distinguishing clicks from drags
      initialMousePosition.current = { x: clientX, y: clientY };
      mouseMoved.current = false;

      const rect = element.getBoundingClientRect();

      // Use custom drag offset if provided (for mobile drag handles), otherwise calculate normally
      if (customDragOffset) {
        dragOffset.current = customDragOffset;
      } else {
        dragOffset.current = {
          x: clientX - rect.left,
          y: clientY - rect.top,
        };
      }

      // Check if this is a mobile touch event on an already selected widget
      const isMobileTouch = e.type === "touchstart" || e.touches;
      const shouldBypassThreshold =
        bypassThreshold || (isMobileTouch && e._mobileDirectDrag);

      if (shouldBypassThreshold) {
        // Start dragging immediately for mobile selected widgets
        mouseMoved.current = true;

        // Store the original positions of all widgets before starting drag
        originalPositions.current = items.reduce((acc, widget) => {
          acc[widget.id] = {
            position: widget.layout[previewMode]?.position,
            startColumn: widget.layout[previewMode]?.startColumn,
            columns: widget.layout[previewMode]?.columns,
            columnHeight: widget.layout[previewMode]?.columnHeight,
          };
          return acc;
        }, {});

        setDraggedItemId(itemId);
        isDragging.current = true;
        setDragPosition({
          x: clientX - dragOffset.current.x,
          y: clientY - dragOffset.current.y,
          width: rect.width,
          height: rect.height,
        });

        // Start handling the actual drag immediately
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("touchmove", handleMouseMove, {
          passive: false,
        });
        return;
      }

      // Set up move handler to detect drag intent (works for both mouse and touch)
      const detectDragIntent = (moveEvent) => {
        // Get coordinates from either mouse or touch event
        const moveClientX =
          moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX;
        const moveClientY =
          moveEvent.clientY ?? moveEvent.touches?.[0]?.clientY;

        if (moveClientX === undefined || moveClientY === undefined) return;

        // Calculate distance moved
        const dx = moveClientX - initialMousePosition.current.x;
        const dy = moveClientY - initialMousePosition.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // If moved more than threshold, consider it a drag attempt
        if (distance > DRAG_THRESHOLD) {
          mouseMoved.current = true;
          moveEvent.preventDefault();

          // Start the drag process
          if (!isDragging.current) {
            // Remove the appropriate event listeners
            document.removeEventListener("mousemove", detectDragIntent);
            document.removeEventListener("touchmove", detectDragIntent);

            // Store the original positions of all widgets before starting drag
            originalPositions.current = items.reduce((acc, widget) => {
              acc[widget.id] = {
                position: widget.layout[previewMode]?.position,
                startColumn: widget.layout[previewMode]?.startColumn,
                columns: widget.layout[previewMode]?.columns,
                columnHeight: widget.layout[previewMode]?.columnHeight,
              };
              return acc;
            }, {});

            setDraggedItemId(itemId);
            isDragging.current = true;
            setDragPosition({
              x: moveClientX - dragOffset.current.x,
              y: moveClientY - dragOffset.current.y,
              width: rect.width,
              height: rect.height,
            });

            // Start handling the actual drag with the main move handler
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("touchmove", handleMouseMove, {
              passive: false,
            });
          }
        }
      };

      // Add both mouse and touch event listeners for initial detection
      document.addEventListener("mousemove", detectDragIntent);
      document.addEventListener("touchmove", detectDragIntent, {
        passive: false,
      });

      // Handle mouse/touch end
      const handleInitialEnd = (upEvent) => {
        document.removeEventListener("mousemove", detectDragIntent);
        document.removeEventListener("touchmove", detectDragIntent);
        document.removeEventListener("mouseup", handleInitialEnd);
        document.removeEventListener("touchend", handleInitialEnd);

        // If didn't move significantly, this was a click/tap not a drag
        if (!mouseMoved.current) {
          // Let the normal click/tap handler work
        }
      };

      document.addEventListener("mouseup", handleInitialEnd);
      document.addEventListener("touchend", handleInitialEnd);
    },
    [items, isSettingsOpen, handleMouseMove, previewMode]
  );

  useEffect(() => {
    if (isDragging.current) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    draggedItemId,
    dragPosition,
    potentialDropArea,
    isDropAnimating,
    handleMouseDown,
    handleWidgetClick: (e) => e.stopPropagation(),
  };
};
